/**
 * PDF file-signature ("magic byte") detection for uploaded documents (BAL-747).
 *
 * The dancer CV upload is direct-to-storage: the browser PUTs the file straight
 * to Supabase, so the create-upload-url action only ever sees client-supplied
 * metadata and its MIME allowlist can be relaxed but never trusted. This is the
 * check that actually looks at the bytes, run at finalize once the object
 * exists.
 */

/**
 * How many leading bytes to read before giving up on finding the header.
 *
 * A conforming PDF starts with `%PDF-` at byte 0, but readers tolerate junk
 * before the header (a mail gateway prepending a banner, a scanner writing a
 * preamble) and files like that are in the wild. 1024 bytes is the same
 * tolerance Adobe's implementation notes describe, and it is small enough that
 * one network chunk almost always covers it.
 */
export const PDF_SNIFF_BYTES = 1024;

/** `%PDF-` — the PDF header, per ISO 32000 §7.5.2. */
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46, 0x2d];

/**
 * Does `head` (the first bytes of a file) contain the PDF header?
 *
 * Scans rather than compares at offset 0, for the leading-junk case above.
 * Returns false for a short read that cannot hold the header at all.
 */
export function looksLikePdf(head: Uint8Array): boolean {
  const lastStart = head.length - PDF_HEADER.length;
  for (let start = 0; start <= lastStart; start++) {
    let matches = true;
    for (let offset = 0; offset < PDF_HEADER.length; offset++) {
      if (head[start + offset] !== PDF_HEADER[offset]) {
        matches = false;
        break;
      }
    }
    if (matches) return true;
  }
  return false;
}
